import { Stack, Box, VStack, Grid } from "@chakra-ui/react";
import { BiPlus } from "react-icons/bi";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import { GradeCriteria, Level } from "../../../../services/Dtos";
import {  useState } from "react";
import Loader from "../../../CustomUi/Loader";
import CustomAlert from "../../../CustomUi/CustomAlert";
import GradeCriteriaModal from "./GradeCriteriaModal";
import GradeCriteriaCard from "./GradeCriteriaCard";
import Empty from "../../../CustomUi/Empty";
import { useNavigate } from "react-router-dom";
import SectionHeader from "../../../CustomUi/SectionHeader";
import useIcons from "../../../../hooks/useIcons";
import CustomTabList from "../../../CustomUi/CustomTabList";
import Cad from "../../../CustomUi/Cad";
import CustomButton from "../../../CustomUi/CustomButton";
import { Tabs } from "@chakra-ui/react";
import { getNextOrder } from "../../../../services/Utils";

function GradeCriteriaSettings() {
  const { gradeCriterias, isLoading, error, levels, accentColor } =
    useSchoolsContext();
  const [gradeLevel, setGradeLevel] = useState<Level | null>(null);

  const icons = useIcons();
  const navigate = useNavigate();

  const [gradeCriteriaToEdit, setGradeCriteriaToEdit] =
    useState<GradeCriteria | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [nextOrder, setNextOrder] = useState(0);


  const handleRefresh = () => {
    // Refresh functionality can be implemented here
  };

  const handleAddGradeCriteria = (level: Level) => {
    setGradeCriteriaToEdit(null);
    setGradeLevel(level);
    setNextOrder(getNextOrder(gradeCriterias.filter((g) => g.levelId === level.id)));
    setIsOpen(true);
  };

  const handleEditGradeCriteria = (gradeCriteria: GradeCriteria) => {
    setGradeCriteriaToEdit(gradeCriteria);
    setGradeLevel(levels.find((l) => l.id === gradeCriteria.levelId) || null);
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setGradeCriteriaToEdit(null);
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <CustomAlert message={error} handleClick={handleRefresh} />;
  }

  return (
    <VStack gap={8} h="100%">
      <GradeCriteriaModal
        isOpen={isOpen}
        onClose={handleCloseModal}
        gradeCriteriaToEdit={gradeCriteriaToEdit}
        gradeLevel={gradeLevel}
        nextOrder={nextOrder}
      />
      {levels.length > 0 ? (
        <Cad minimal>
          <VStack gap={6} align="stretch">
            <SectionHeader
              title="Grade Criteria"
              subtitle="Manage grade criteria for all school levels"
              icon={icons.main.grade}
            />
            {/* Tabs for Levels */}
            <Tabs.Root defaultValue={levels[0]?.id || ""}>
              <CustomTabList
                items={levels.map((level) => ({
                  id: level.id,
                  label: level.levelName,
                }))}
                colorPalette={accentColor}
              >
                {(item) => {
                  const level = levels.find((l) => l.id === item.id);
                  if (!level) return null;

                  return (
                    <VStack gap={6} align="stretch">
                      {/* Level Header */}
                      <Stack
                        direction={{ base: "column", md: "row" }}
                        align={{ base: "stretch", md: "center" }}
                        justify="flex-end"
                        w="full"
                        gap={3}
                      >
                        <CustomButton
                          colorPalette={accentColor}
                          label="Add Grade Criteria"
                          icon={BiPlus}
                          onClick={() => handleAddGradeCriteria(level)}
                          w={{ base: "100%", md: "auto" }}
                        />
                      </Stack>
                      {/* Grade Criteria Grid */}
                      {gradeCriterias.filter((g) => g.levelId === level.id)
                        .length > 0 ? (
                        <Grid
                          templateColumns={{
                            base: "1fr",
                            lg: "repeat(2, 1fr)",
                            xl: "repeat(3, 1fr)",
                          }}
                          gap={6}
                        >
                          {gradeCriterias
                            .filter((g) => g.levelId === level.id)
                            .map((s) => (
                              <GradeCriteriaCard
                                key={s.id}
                                gradeCriteria={s}
                                handleClick={() => handleEditGradeCriteria(s)}
                              />
                            ))}
                        </Grid>
                      ) : (
                        <Box py={8}>
                          <Empty
                            message={`No grade criteria created for ${level.levelName} yet`}
                            hasButton
                            buttonText={`Create First Grade Criteria`}
                            handleClick={() => handleAddGradeCriteria(level)}
                          />
                        </Box>
                      )}
                    </VStack>
                  );
                }}
              </CustomTabList>
            </Tabs.Root>
          </VStack>
        </Cad>
      ) : (
        <Cad minimal>
          <Empty
            hasButton
            message="Something will show up here when you set up School Levels"
            buttonText="Go to Levels Settings"
            handleClick={() => navigate("/settings/level")}
          />
        </Cad>
      )}
    </VStack>
  );
}

export default GradeCriteriaSettings;
